import {Fragment, useEffect, useState} from 'react';

import {
  Navbar,
  NavbarDivider,
  NavbarItem,
  NavbarLabel,
  NavbarSection,
  NavbarSpacer
} from '@nlabs/gothamjs';

import {navigate} from '../lib/navigation';
import {Route} from '../types/routes';
import {Brand} from './Brand';

type HeaderNavItem = {
  readonly dividerBefore?: boolean;
  readonly href?: string;
  readonly label: string;
  readonly route?: Route;
};

const navItems: HeaderNavItem[] = [
  {label: 'Home', route: '/'},
  {href: '/#programs', label: 'Programs'},
  {href: '/#impact', label: 'Impact'},
  {href: '/#events', label: 'Events'},
  {href: '/#stories', label: 'Stories'},
  {href: '/#about', label: 'About'}
];

const isCurrentRoute = (route: Route, itemRoute: Route) => (
  itemRoute === '/projects' ? route.startsWith('/projects') : route === itemRoute
);

const getHash = () => window.location.hash;

const getItemHash = (href?: string) => {
  const hashIndex = href?.indexOf('#') ?? -1;
  return hashIndex >= 0 ? href?.slice(hashIndex) : undefined;
};

const sectionIds = navItems
  .map((item) => getItemHash(item.href)?.slice(1))
  .filter((id): id is string => Boolean(id));

const getActiveHashFromScroll = () => {
  const threshold = 110;
  const sections = sectionIds
    .map((id) => document.getElementById(id))
    .filter((section): section is HTMLElement => Boolean(section))
    .sort((a, b) => a.offsetTop - b.offsetTop);

  let activeHash = '';

  for(const section of sections) {
    if(section.getBoundingClientRect().top <= threshold) {
      activeHash = `#${section.id}`;
    } else {
      break;
    }
  }

  return activeHash;
};

const isCurrentNavItem = (route: Route, activeHash: string, item: HeaderNavItem) => {
  const itemHash = getItemHash(item.href);

  if(itemHash) return route === '/' && activeHash === itemHash;
  if(item.route === '/') return route === '/' && activeHash.length === 0;
  if(item.route) return activeHash.length === 0 && isCurrentRoute(route, item.route);
  return false;
};

const hasLightHero = (route: Route) => (
  route === '/' || route.startsWith('/projects') || route === '/creators' || route === '/portal' || route === '/support'
);

const navbarClassName = (route: Route) => [
  '-mb-[72px] min-h-[72px] gap-3 border-b border-transparent bg-transparent px-[clamp(18px,5vw,72px)] text-white transition-[background-color,backdrop-filter,border-color] duration-200',
  '[&[data-scroll-state=at-top]]:[backdrop-filter:none] [&[data-scroll-state=at-top]]:bg-transparent [&[data-scroll-state=at-top]]:border-transparent',
  '[&[data-scroll-state=scrolled]]:[backdrop-filter:blur(16px)] [&[data-scroll-state=scrolled]]:bg-white/60 [&[data-scroll-state=scrolled]]:border-[rgba(16,22,36,.14)] [&[data-scroll-state=scrolled]_[data-slot=navbar-item]]:text-[rgba(16,22,36,.72)] [&[data-scroll-state=scrolled]_[data-slot=navbar-item]:hover]:text-[var(--nx-ink)] [&[data-scroll-state=scrolled]_[data-slot=navbar-item][data-current=true]]:text-[var(--nx-ink)]',
  hasLightHero(route) ? '[&[data-scroll-state=at-top]]:text-[var(--nx-ink)] [&[data-scroll-state=at-top]_[data-slot=navbar-item]]:text-[rgba(16,22,36,.76)] [&[data-scroll-state=at-top]_[data-slot=navbar-item]:hover]:text-[var(--nx-ink)] [&[data-scroll-state=at-top]_[data-slot=navbar-item][data-current=true]]:text-[var(--nx-ink)] [&[data-scroll-state=at-top_button]:text-[var(--nx-ink)]' : ''
].join(' ');

const renderNavItems = (route: Route, activeHash: string, isMobile = false) => navItems.map((item) => (
  <Fragment key={item.route || item.href}>
    {item.dividerBefore ? <NavbarDivider className={isMobile ? 'my-1 h-px w-full bg-[rgba(16,22,36,.12)]' : 'mx-2 h-6 w-px bg-current opacity-30'} /> : null}
    <NavbarItem
      className={isMobile
        ? 'min-h-11 justify-start rounded-[var(--nx-radius)] bg-transparent px-3 text-base font-bold text-current hover:bg-current/10 data-[current=true]:bg-current/15 data-[current=true]:text-current after:hidden'
        : 'justify-center rounded-[var(--nx-radius)] bg-transparent px-3 text-center text-sm font-bold text-white/70 hover:text-white data-[current=true]:text-white after:absolute after:bottom-[-16px] after:left-1/2 after:h-0.5 after:w-[64px] after:-translate-x-1/2 after:rounded-full after:bg-current after:opacity-0 data-[current=true]:after:opacity-100 [&_[data-slot=navbar-label]]:overflow-visible [&_[data-slot=navbar-label]]:text-clip'}
      current={isCurrentNavItem(route, activeHash, item)}
      href={item.href}
      onClick={item.route ? () => navigate(item.route as Route) : undefined}
    >
      <NavbarLabel>{item.label}</NavbarLabel>
    </NavbarItem>
  </Fragment>
));

export const SiteHeader = ({route}: {route: Route}) => {
  const [activeHash, setActiveHash] = useState(getHash);

  useEffect(() => {
    const handleLocationChange = () => setActiveHash(getHash());
    window.addEventListener('hashchange', handleLocationChange);
    window.addEventListener('popstate', handleLocationChange);
    return () => {
      window.removeEventListener('hashchange', handleLocationChange);
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  useEffect(() => {
    if(route !== '/') return undefined;

    let animationFrame = 0;
    const updateActiveHash = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        setActiveHash(getActiveHashFromScroll());
      });
    };

    updateActiveHash();
    window.addEventListener('scroll', updateActiveHash, {passive: true});
    window.addEventListener('resize', updateActiveHash);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('scroll', updateActiveHash);
      window.removeEventListener('resize', updateActiveHash);
    };
  }, [route]);

  return (
    <Navbar
      className={navbarClassName(route)}
      isSticky
      mobileMenu={<nav className="grid gap-1" aria-label="Mobile site navigation">{renderNavItems(route, activeHash, true)}</nav>}
      mobileMenuTitle="UFAT"
      transparentOnScroll
      transparentScrollThreshold={12}>
      <NavbarSection>
        <Brand />
      </NavbarSection>
      <NavbarSpacer className="flex-1" />
      <NavbarSection className="ml-auto hidden flex-wrap justify-end lg:flex">
        {renderNavItems(route, activeHash)}
      </NavbarSection>
    </Navbar>
  );
};
